import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCatalog, useCreateCatalog, useDeleteCatalog, useUpdateCatalog } from '@/lib/api/catalogs';
import { ApiError } from '@/lib/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { ImportsPanel } from '@/features/catalogs/ImportsPanel';
import { ProductsPanel } from '@/features/catalogs/ProductsPanel';
import { useWorkspacePath } from '@/lib/workspacePath';

export function CatalogFormPage() {
  // The URL segment addresses the catalog by slug (spec 0013); a UUID also
  // resolves (AC-9). The detail GET resolves it once; its UUID (`catalogId`)
  // drives the nested product/import calls and delete (ADR-0002).
  const { catalogRef } = useParams<{ catalogRef: string }>();
  const isEdit = Boolean(catalogRef);
  const navigate = useNavigate();
  const ws = useWorkspacePath();

  const existing = useCatalog(catalogRef);
  const catalogId = existing.data?.id;
  const createCatalog = useCreateCatalog();
  const updateCatalog = useUpdateCatalog(catalogId ?? '');
  const deleteCatalog = useDeleteCatalog();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loadedFromServer, setLoadedFromServer] = useState(false);

  useEffect(() => {
    if (!existing.data || loadedFromServer) return;
    setName(existing.data.name);
    setDescription(existing.data.description);
    setLoadedFromServer(true);
  }, [existing.data, loadedFromServer]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isEdit && catalogId) {
      updateCatalog.mutate({ name, description });
    } else {
      createCatalog.mutate({ name, description }, { onSuccess: () => navigate(ws('catalogs')) });
    }
  }

  function handleDelete() {
    if (!catalogId || !existing.data) return;
    const count = existing.data.product_count;
    const message =
      count > 0
        ? `Delete "${existing.data.name}"? This will also delete its ${count} product(s).`
        : `Delete "${existing.data.name}"?`;
    if (!window.confirm(message)) return;
    deleteCatalog.mutate(catalogId, { onSuccess: () => navigate(ws('catalogs')) });
  }

  const mutation = isEdit ? updateCatalog : createCatalog;

  if (isEdit && existing.isLoading) {
    return <Spinner label="Loading catalog" />;
  }

  // An unknown catalog slug/UUID under the active workspace is a not-found state
  // (spec 0013, AC-11) -- resolution stays inside the workspace RLS session.
  if (isEdit && existing.error) {
    const notFound = existing.error instanceof ApiError && existing.error.status === 404;
    return (
      <div
        role="alert"
        data-testid="catalog-not-found"
        className="rounded-md border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700"
      >
        <p>{notFound ? 'Catalog not found.' : 'Failed to load this catalog.'}</p>
        <Link
          to={ws('catalogs')}
          className="mt-2 inline-block font-medium text-brand-700 hover:underline"
        >
          Back to catalogs
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card title={isEdit ? 'Edit catalog' : 'New catalog'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="catalog-name-input"
          />
          <Textarea
            label="Description (written for the agent)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            required
            placeholder="Describe what this catalog contains so an agent can decide when to search it."
            data-testid="catalog-description-input"
          />

          {mutation.error && (
            <p
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="flex items-center justify-between">
            <Button type="submit" disabled={mutation.isPending} data-testid="save-catalog-button">
              {mutation.isPending ? 'Saving...' : isEdit ? 'Save changes' : 'Create catalog'}
            </Button>
            {isEdit && (
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                data-testid="delete-catalog-button"
              >
                Delete
              </Button>
            )}
          </div>
        </form>
      </Card>

      {isEdit && catalogId && <ImportsPanel catalogId={catalogId} />}
      {isEdit && catalogId && <ProductsPanel catalogId={catalogId} />}
    </div>
  );
}
